import { Autocomplete, Box, Grid, TextField, Typography, useTheme } from '@mui/material';
import React, { BaseSyntheticEvent, useContext, useEffect, useMemo, useState } from 'react';

import { ApplicationCycleContext } from '@/Contexts/ApplicationCycleContext';
import { ApplicationCycle } from '@/models/ApplicationCycle';
import { ApplicationTypes } from '@/models/ApplicationType';
import { PartnerProps } from '@/models/Partner';
import { PartnerYearsRecognized } from '@/models/PartnerYearsRecognized';
import { ProgramNames } from '@/models/Program';
import { SelectOptionProps } from '@/models/SelectOption';

export const PartnerYearsRecognizedRow = ({
  partnerInfo,
  programId,
  applicationTypeId,
  onDraftPartnerYearsRecognizedChange,
}: {
  partnerInfo: PartnerProps;
  programId: ProgramNames;
  applicationTypeId: ApplicationTypes;
  onDraftPartnerYearsRecognizedChange: (partner: PartnerProps) => void;
}) => {
  const theme = useTheme();
  const { applicationCycles } = useContext(ApplicationCycleContext);
  const [yearsAwarded, setYearsAwarded] = useState<ApplicationCycle[]>([]);

  const eligibleApplicationCycleOptions: SelectOptionProps[] = useMemo(() => {
    let filteredAppCycles = applicationCycles;

    filteredAppCycles = filteredAppCycles.filter(
      (appCycle) =>
        appCycle.programId === programId &&
        appCycle.applicationTypeId === applicationTypeId &&
        new Date(appCycle.closeDate) < new Date()
    );

    if (
      applicationTypeId === ApplicationTypes.GrowthAward &&
      partnerInfo.partnerYearsRecognized != null
    ) {
      const standardAwardedYears = new Set(
        partnerInfo.partnerYearsRecognized
          .filter(
            (pyr) =>
              pyr.applicationTypeId === ApplicationTypes.Standard && pyr.programId === programId
          )
          .map((pyr) => applicationCycles.find((ac) => ac.id === pyr.applicationCycleId)?.year)
          .filter(Boolean)
      );

      filteredAppCycles = filteredAppCycles.filter((appCycle) =>
        standardAwardedYears.has(appCycle.year)
      );
    }

    return filteredAppCycles
      .sort((a, b) => b.year - a.year)
      .map((appCycle: ApplicationCycle) => ({
        label: appCycle.applicationCycleName,
        value: appCycle.year,
      }));
  }, [applicationCycles, applicationTypeId, programId, partnerInfo.partnerYearsRecognized]);

  const formatYearsRecognized = (years: ApplicationCycle[]): PartnerYearsRecognized[] =>
    years.map((year) => ({
      partnerId: partnerInfo.id,
      programId,
      applicationTypeId,
      applicationCycleId: year.id,
    })) as PartnerYearsRecognized[];

  const setDraftYears = (newYears: ApplicationCycle[]) => {
    setYearsAwarded(newYears);

    const otherProgramYears = (partnerInfo.partnerYearsRecognized ?? []).filter(
      (pyr) => !(pyr.applicationTypeId === applicationTypeId && pyr.programId === programId)
    );

    onDraftPartnerYearsRecognizedChange({
      ...partnerInfo,
      partnerYearsRecognized: [...otherProgramYears, ...formatYearsRecognized(newYears)],
    });
  };

  useEffect(() => {
    if (partnerInfo?.partnerYearsRecognized == null) {
      setYearsAwarded([]);
      return;
    }

    const awardedIds = partnerInfo.partnerYearsRecognized
      .filter((pyr) => pyr.applicationTypeId === applicationTypeId && pyr.programId === programId)
      .map((pyr) => pyr.applicationCycleId);

    const years = applicationCycles
      .filter((ac) => awardedIds.includes(ac.id))
      .sort((a, b) => b.year - a.year);

    setYearsAwarded(years);
  }, [applicationCycles, applicationTypeId, partnerInfo.partnerYearsRecognized, programId]);

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.sandShade3.main}`,
        borderRadius: 1,
        p: 2,
        mb: 2,
      }}
    >
      <Autocomplete
        size='small'
        multiple
        disableCloseOnSelect
        disableClearable
        renderInput={(params) => <TextField {...params} label='Select Years' fullWidth />}
        getOptionLabel={(option) => option.label}
        value={yearsAwarded.map((year) => ({
          label: year.applicationCycleName,
          value: year.year,
        }))}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        options={applicationCycles != null ? eligibleApplicationCycleOptions : []}
        onChange={(_event: BaseSyntheticEvent, newValue: SelectOptionProps[]) => {
          const newYears = newValue
            .map((year: SelectOptionProps) =>
              applicationCycles.find(
                (ac) =>
                  ac.year === year.value &&
                  ac.programId === programId &&
                  ac.applicationTypeId === applicationTypeId
              )
            )
            .filter(Boolean) as ApplicationCycle[];

          setDraftYears(newYears);
        }}
        fullWidth
        sx={{ whiteSpace: 'pre-wrap', minWidth: '300px', pb: 2 }}
      />

      {yearsAwarded.length > 0 ? (
        <Grid container spacing={2} sx={{ color: theme.palette.taiga.main }}>
          {yearsAwarded.map((value) => (
            <Grid size={{ xs: 12, sm: 3 }} key={value.year} sx={{ p: 0.5 }}>
              <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                {value.applicationCycleName}
              </Typography>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography>No Years Recognized</Typography>
      )}
    </Box>
  );
};
